import { Component, OnInit } from '@angular/core';
import { MetricsService } from '../../services/metrics.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Chart } from 'chart.js';
import { EventsService } from '../../services/events.service';
import { Tag } from '../../interfaces/tag';
import { isEqual } from 'lodash';
import { TagsServiceTypeEnum } from '../../enums/tags-service-type.enum';

@Component({
  selector: 'app-metrics',
  templateUrl: './metrics.component.html',
  styleUrls: ['./metrics.component.scss']
})
export class MetricsComponent implements OnInit {
  private static readonly SALES_METRIC_TOTAL_CANVAS_ID = 'salesMetricTotalCanvas';
  private static readonly SALES_METRIC_COUNT_CANVAS_ID = 'salesMetricCountCanvas';
  public readonly tagsServiceTypeEnum = TagsServiceTypeEnum;
  public isLoadingResults = false;
  public metricsForm: FormGroup;
  public salesMetricTotalChart = [];
  public salesMetricCountChart = [];
  public eventTypes: any[];
  public totalCharts: any[] = [];
  public countCharts: any[] = [];
  public serviceName = EventsService.name;

  private static readonly FINANCIAL_FORMATTER = (value: number) =>
    value.toLocaleString('pl', { minimumFractionDigits: 2 }) + 'zł';
  private static readonly NUMBER_FORMATTER = (value: number) =>
    value.toLocaleString('pl', { minimumFractionDigits: 2 });
  private static readonly INT_FORMATTER = (value: number) => value.toLocaleString('pl', { minimumFractionDigits: 0 });

  static initForm() {
    return new FormGroup({
      tags: new FormControl('', [Validators.required])
    });
  }

  constructor(private metricsService: MetricsService, private eventsService: EventsService) {
    this.metricsForm = MetricsComponent.initForm();
  }

  ngOnInit() {
    this.displayMetrics([]);
  }

  displayMetrics(tags: string[]) {
    this.isLoadingResults = true;
    this.eventsService.getEventTypes(tags).subscribe(eventTypes => {
      this.eventTypes = eventTypes;
      this.eventTypes.forEach((eventType, eventTypeIndex) => {
        this.metricsService.getMetric([eventType.eventType], tags).subscribe(metricData => {
          const totalChart = this.createTotalChart(metricData, eventTypeIndex);
          this.totalCharts.push(totalChart);

          const countChart = this.createCountChart(metricData, eventTypeIndex);
          this.countCharts.push(countChart);

          this.isLoadingResults = false;
        });
      });
    });
  }

  filterByTags($event: Tag[]): void {
    const tags = $event.length ? $event.map(tag => tag.value) : [];
    this.displayMetrics(tags);
  }

  public updateCharts(metricsForm: FormGroup): void {
    const chartParams = {
      tags: metricsForm.value.minNetworkSize
    };

    this.isLoadingResults = true;
    this.eventTypes.forEach((eventType, eventTypeIndex) => {
      this.metricsService.getMetric([eventType.eventType]).subscribe(metricData => {
        this.totalCharts[eventTypeIndex]['data']['datasets']['data'] = metricData.map(entry => entry['totalValue']);
        this.countCharts[eventTypeIndex]['data']['datasets']['data'] = metricData.map(entry => entry['count']);
        this.isLoadingResults = false;
      });
    });
  }

  private createTotalChart(metricData: any[], index) {
    const values = metricData.map(entry => entry['totalValue']);
    const labels = metricData.map(entry => `${entry['year']}-${entry['month']}`);

    const datasets = [
      {
        data: values,
        backgroundColor: 'rgba(0,200,83,0.5)',
        borderColor: '#00C853'
      }
    ];

    return this.createLineChart(
      { labels: labels, datasets: datasets },
      `totalChart${index}`,
      MetricsComponent.FINANCIAL_FORMATTER
    );
  }

  private createCountChart(metricData: any[], index) {
    const values = metricData.map(entry => entry['count']);
    const labels = metricData.map(entry => `${entry['year']}-${entry['month']}`);

    const datasets = [
      {
        data: values,
        backgroundColor: 'rgba(0,200,83,0.5)',
        borderColor: '#00C853'
      }
    ];

    return this.createLineChart(
      { labels: labels, datasets: datasets },
      `countChart${index}`,
      MetricsComponent.INT_FORMATTER
    );
  }

  private createLineChart(chartData, canvasElementId, formatter: (value: number) => string): Chart {
    return new Chart(canvasElementId, {
      type: 'line',
      data: chartData,
      options: {
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              return formatter(Number(tooltipItem.yLabel));
            }
          }
        },
        scales: {
          xAxes: [
            {
              display: true,
              ticks: {
                beginAtZero: true
              }
            }
          ],
          yAxes: [
            {
              display: true,
              ticks: {
                beginAtZero: true,
                callback: function(value, index, values) {
                  return formatter(value);
                }
              }
            }
          ]
        }
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/metrics/metrics.component.ts